import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  StarIcon as StarSolidIcon,
  CheckIcon,
  CheckCircleIcon,
  XMarkIcon,
  PaperClipIcon,
  ExclamationTriangleIcon,
  ShieldExclamationIcon,
} from "@heroicons/react/24/solid";
import { StarIcon as StarOutlineIcon } from "@heroicons/react/24/outline";
import {
  Avatar,
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@heroui/react";

import {
  DealDisputeRequestDto,
  DealResponseDto,
  FileMetadataDto,
} from "../../types/types";
import { updateFightResultOnly, uploadMultipleFile } from "../../services/api";
import { useNavigate } from "react-router-dom";
import { VIDEO_UPLOAD_ENABLED } from "../../config/featureFlags";

type FightResultType = string | null;

type ResultOption = {
  value: string;
  label: string;
};

type TemplateLabels = {
  fieldLabels: Record<string, string>;
  optionLabels: Record<string, Record<string, string>>;
};

interface ResultRatingsTabProps {
  deal: DealResponseDto;
  activeTab: string;
  currentUserId: string;
  isManager: boolean;
  hasUserRated: boolean;
  refreshDeal: () => void;
  tr: (value?: string) => string; // 👈 புது prop type add பண்ணிட்டோம்
}

const RESULT_TRANSLATION_KEYS: Record<string, string> = {
  WIN: "win",
  LOSS: "loss",
  DRAW: "draw",
  NO_SHOW_FAULT: "noShowFault",
  NO_SHOW_MEDICAL: "noShowMedical",
  EVENT_CANCELLED: "eventCancelled",
  OPPONENT_NO_SHOW: "opponentNoShow",
};

const emptyTemplateLabels: TemplateLabels = {
  fieldLabels: {},
  optionLabels: {},
};

const isVideoFile = (file: File): boolean =>
  file.type.startsWith("video/") || /\.(mp4|mov|webm|mkv|avi)$/i.test(file.name);

const getFightResult = (deal: DealResponseDto): string | undefined =>
  deal.fightResult ?? undefined;

const isManagerAccepted = (deal: DealResponseDto): boolean =>
  [
    "RESULT_UPDATED",
    "RESULT_CONFIRMED",
    "FIGHTER_RATED",
    "MANAGER_RATED",
    "FINISHED",
  ].includes(deal.status ?? "");

const isFighterAccepted = (deal: DealResponseDto): boolean =>
  ["RESULT_CONFIRMED", "FIGHTER_RATED", "MANAGER_RATED", "FINISHED"].includes(
    deal.status ?? "",
  );

const formatAmount = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "-";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(
    value,
  );
};

const toTitleCase = (value: string): string =>
  String(value)
    .replace(/^(componentLabels|paymentTiming)\./i, "")
    .replace(/^payoutTypes\./i, "")
    .replace(/\.(text|hint)$/i, "")
    .replace(/[_.-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

const getFirstText = (...values: any[]): string => {
  const value = values.find(
    (item) => item !== undefined && item !== null && String(item).trim() !== "",
  );
  return value ? String(value).trim() : "";
};

const getTranslation = (t: any, key: string): string => {
  if (!key) return "";
  const translated = t(key);
  return typeof translated === "string" && translated !== key ? translated : "";
};

const getTemplateStepId = (step: any): string =>
  String(
    step?.stepId ||
      step?.id ||
      step?.key ||
      String(step?.title || "step")
        .toLowerCase()
        .replace(/\s+/g, "_"),
  );

const getOptionValue = (option: any): string => {
  if (typeof option === "string") return option;
  return String(option?.value ?? option?.key ?? option?.id ?? "");
};

const getOptionLabel = (option: any): string => {
  if (typeof option === "string") return option;
  return String(
    option?.label ??
      option?.title ??
      option?.text ??
      option?.name ??
      option?.value ??
      option?.key ??
      "",
  );
};

const addOptionLabel = (
  labels: TemplateLabels,
  fieldPath: string,
  value: string,
  label: string,
) => {
  if (!fieldPath || !value || !label) return;
  if (!labels.optionLabels[fieldPath]) labels.optionLabels[fieldPath] = {};
  labels.optionLabels[fieldPath][value] = label;
  labels.optionLabels[fieldPath][value.toLowerCase()] = label;
  labels.optionLabels[fieldPath][value.toUpperCase()] = label;
};

const buildTemplateLabels = (templateJson?: any): TemplateLabels => {
  const steps = Array.isArray(templateJson?.steps) ? templateJson.steps : [];
  if (steps.length === 0) return emptyTemplateLabels;

  const labels: TemplateLabels = {
    fieldLabels: {},
    optionLabels: {},
  };

  const registerField = (field: any, fieldPath: string, stepId: string) => {
    const fieldLabel = getFirstText(field?.label, field?.title, field?.name);
    if (fieldLabel) labels.fieldLabels[fieldPath] = fieldLabel;

    if (Array.isArray(field?.options)) {
      field.options.forEach((option: any) => {
        addOptionLabel(labels, fieldPath, getOptionValue(option), getOptionLabel(option));
      });
    }

    if (Array.isArray(field?.fields)) {
      field.fields.forEach((nestedField: any) => {
        const nestedKey = String(nestedField?.key || "");
        if (!nestedKey) return;
        registerField(nestedField, `${fieldPath}.${nestedKey}`, stepId);
      });
    }
  };

  steps.forEach((step: any) => {
    const stepId = getTemplateStepId(step);
    if (!Array.isArray(step?.fields)) return;

    step.fields.forEach((field: any) => {
      const fieldKey = String(field?.key || "");
      if (!fieldKey) return;
      registerField(field, `${stepId}.${fieldKey}`, stepId);
    });
  });

  return labels;
};

const getTemplateOptionLabel = (
  labels: TemplateLabels,
  fieldPath: string,
  value?: string | null,
): string => {
  const key = String(value || "");
  if (!key) return "";

  const options = labels.optionLabels[fieldPath];
  return options?.[key] || options?.[key.toLowerCase()] || options?.[key.toUpperCase()] || "";
};

const getResultDisplayLabel = (
  resultKey: string | null | undefined,
  scenario: any,
  t: any,
): string => {
  const key = String(resultKey || "").trim();
  if (!key) return "-";

  const upperKey = key.toUpperCase();
  const translated =
    getTranslation(t, RESULT_TRANSLATION_KEYS[upperKey]) ||
    getTranslation(t, `fightResults.${upperKey}`);

  return translated || getFirstText(scenario?.scenarioLabel) || toTitleCase(key);
};

const getPayoutTypeDisplayLabel = (scenario: any, t: any): string => {
  const code = getFirstText(scenario?.appliedPayoutType, scenario?.payoutType);
  const translated =
    getTranslation(t, `payoutSelectionModes.${code}`) ||
    getTranslation(t, `payoutTypes.${code.toLowerCase()}.text`);

  return (
    translated ||
    getFirstText(scenario?.appliedPayoutTypeLabel, scenario?.payoutTypeLabel) ||
    (code ? toTitleCase(code) : "-")
  );
};

const getComponentDisplayLabel = (
  component: any,
  labels: TemplateLabels,
  t: any,
): string => {
  const code = getFirstText(
    component?.componentType,
    component?.type,
    component?.key,
    component?.value,
  );

  return (
    getTemplateOptionLabel(labels, "components.componentType", code) ||
    getFirstText(
      component?.componentLabel,
      component?.label,
      component?.displayName,
      component?.name,
    ) ||
    getTranslation(t, `componentLabels.${code}`) ||
    (code ? toTitleCase(code) : "-")
  );
};

const getPaymentTimingDisplayLabel = (
  component: any,
  labels: TemplateLabels,
  t: any,
): string => {
  const code = getFirstText(
    component?.paymentTiming,
    component?.timing,
    component?.fundingStage,
  );

  return (
    getTemplateOptionLabel(labels, "components.fundingStage", code) ||
    getFirstText(component?.paymentTimingLabel, component?.timingLabel) ||
    getTranslation(t, `paymentTiming.${code}`) ||
    (code ? toTitleCase(code) : "")
  );
};

const buildResultOptions = (deal: DealResponseDto, t: any): ResultOption[] => {
  const scenarios = deal?.dealPolicySummaryDto?.scenarios ?? [];
  const options: ResultOption[] = [];

  scenarios.forEach((scenario: any) => {
    const value = getFirstText(scenario?.scenario);
    if (!value || options.some((option) => option.value === value)) return;

    options.push({
      value,
      label: getResultDisplayLabel(value, scenario, t),
    });
  });

  if (options.length === 0 && deal?.fightResult) {
    options.push({
      value: deal.fightResult,
      label: getResultDisplayLabel(deal.fightResult, null, t),
    });
  }

  return options;
};

const resolveScenario = (
  deal: DealResponseDto,
  resultKey: string | null | undefined,
): any => {
  if (!resultKey) return null;

  const scenarios = deal?.dealPolicySummaryDto?.scenarios ?? [];

  const matched =
    scenarios.find(
      (s: any) =>
        typeof s.scenario === "string" &&
        s.scenario.toLowerCase().trim() === resultKey.toLowerCase().trim(),
    ) ?? null;

  return matched;
};

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mb-1.5 text-[14px] font-semibold tracking-wide text-slate-700">
    {children}
  </p>
);

const Rule: React.FC = () => <div className="border-t border-slate-200" />;

const ResultBadge: React.FC<{
  label: string;
}> = ({ label }) => (
  <span className="inline-flex items-center rounded-md border border-sky-200 bg-sky-50 px-3 py-1 text-[14px] font-semibold text-sky-800">
    {label}
  </span>
);

interface SelectDropdownProps {
  value: FightResultType;
  onChange: (val: FightResultType) => void;
  options: ResultOption[];
  disabled?: boolean;
  placeholder?: string;
}

const SelectDropdown: React.FC<SelectDropdownProps> = ({
  value,
  onChange,
  options,
  disabled = false,
  placeholder = "",
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((p) => !p)}
        className={[
          "flex w-full items-center justify-between rounded-xl border px-3 py-2 text-[14px] font-medium transition-all",
          disabled
            ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
            : "cursor-pointer border-slate-300 bg-white text-slate-900 hover:border-sky-300 hover:bg-sky-50/30",
        ].join(" ")}
      >
        <span className="flex min-w-0 items-center gap-2">
          {value ? (
            <>
              <CheckIcon className="h-4 w-4 shrink-0 text-sky-500" />
              <span className="truncate font-semibold">
                {options.find((option) => option.value === value)?.label ||
                  toTitleCase(value)}
              </span>
            </>
          ) : (
            <span className="truncate text-slate-500">{placeholder}</span>
          )}
        </span>
        <svg
          className={`h-4 w-4 shrink-0 transition-transform duration-200 ${
            open ? "rotate-180 text-sky-500" : "text-slate-400"
          }`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
              className={[
                "flex w-full items-center gap-2 px-3 py-2 text-left text-[14px] transition-colors",
                value === option.value
                  ? "bg-sky-50 text-slate-900 font-semibold"
                  : "text-slate-700 hover:bg-sky-50/60",
              ].join(" ")}
            >
              {value === option.value ? (
                <CheckIcon className="h-4 w-4 shrink-0 text-sky-500" />
              ) : (
                <span className="h-4 w-4 shrink-0" />
              )}
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

interface ScenarioCardProps {
  scenario: any;
  baseAmount: number | null | undefined;
  currency: string;
  isManager: boolean;
  isDraw: boolean;
  t: any;
  templateLabels: TemplateLabels;
  tr: (value?: string) => string; // 👈 புது prop type add பண்ணிட்டோம்
}

const ScenarioCard: React.FC<ScenarioCardProps> = ({
  scenario,
  baseAmount,
  currency,
  isManager,
  isDraw,
  t,
  templateLabels,
  tr, // 👈 புது parameter destructure பண்ணிட்டோம்
}) => {
  const primaryAmount = isManager
    ? scenario.refundAmount
    : scenario.payoutAmount;
  const primaryLabel = isManager
    ? t("refundAmount")
    : t("earnings");
  const scenarioLabel = getResultDisplayLabel(scenario.scenario, scenario, t);
  const payoutTypeText = getPayoutTypeDisplayLabel(scenario, t);
  const payoutTypeHint =
    scenario.appliedPayoutTypeHint ||
    scenario.payoutTypeHint ||
    "";
  const included = (scenario.includedComponents ?? []) as any[];
  const excluded = (scenario.excludedComponents ?? []) as any[];

  return (
    <div className="w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="border-b border-slate-100 bg-gradient-to-r from-sky-50/60 via-white to-indigo-50/50 p-5">
        <div className="mb-2 flex items-start justify-between gap-2">
          <Label>{primaryLabel}</Label>
          <span className="shrink-0 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-[12px] font-semibold text-slate-600">
            {scenarioLabel}
          </span>
        </div>

        <p className="text-[32px] font-bold tracking-tight text-slate-950">
          {formatAmount(primaryAmount)}{" "}
          <span className="text-base font-medium text-slate-600">
            {currency}
          </span>
        </p>

        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          <span>{payoutTypeText}</span>
          {payoutTypeHint && (
            <span className="text-[13px] font-medium text-slate-600">
              {tr(payoutTypeHint)}
              {/* 👆 OLD-ல இது இப்படி இருந்துச்சு: {payoutTypeHint}
                 NEW-ல tr() போட்டு wrap பண்ணிட்டோம் */}
            </span>
          )}
        </div>
      </div>

      <div className="space-y-1 p-5">
        <Label>{t("breakdown")}</Label>

        <div className="flex items-center justify-between py-1.5">
          <span className="text-[15px] font-medium text-slate-700">
            {t("baseAmount")}
          </span>
          <span>
            {formatAmount(baseAmount)} {currency}
          </span>
        </div>

        {included.map((comp: any, i: number) => (
          <div
            key={`inc-${i}`}
            className="flex items-center justify-between py-1.5"
          >
            <div className="flex min-w-0 items-center gap-2">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-sky-400" />
              <span className="truncate text-[15px] font-medium text-slate-700">
                {getComponentDisplayLabel(comp, templateLabels, t)}
              </span>
              <span className="shrink-0 text-[12px] font-medium capitalize text-slate-500">
                {getPaymentTimingDisplayLabel(comp, templateLabels, t)}
              </span>
            </div>
            <span className="ml-3 shrink-0 text-[15px] font-semibold text-slate-950">
              +{formatAmount(comp.resolvedAmount)}{" "}
              <span className="text-sm font-medium text-slate-600">
                {currency}
              </span>
            </span>
          </div>
        ))}

        {excluded.map((comp: any, i: number) => (
          <div
            key={`exc-${i}`}
            className="flex items-center justify-between py-1.5"
          >
            <div className="flex min-w-0 items-center gap-2">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-slate-300" />
              <span className="truncate text-[15px] font-medium text-slate-500 line-through">
                {getComponentDisplayLabel(comp, templateLabels, t)}
              </span>
              <span className="shrink-0 text-[12px] font-medium capitalize text-slate-500">
                {getPaymentTimingDisplayLabel(comp, templateLabels, t)}
              </span>
            </div>
            <span className="ml-3 shrink-0 text-[15px] font-medium text-slate-500 line-through">
              {formatAmount(comp.resolvedAmount)}{" "}
              <span className="text-sm font-medium text-slate-500">
                {currency}
              </span>
            </span>
          </div>
        ))}

        <div className="mt-1 flex items-center justify-between border-t border-slate-200 pt-3">
          <span className="text-[13px] font-semibold text-slate-600">
            {t("total")}
          </span>
          <span className="text-[16px] font-bold text-slate-950">
            {formatAmount(primaryAmount)}{" "}
            <span className="text-sm font-medium text-slate-600">
              {currency}
            </span>
          </span>
        </div>
      </div>

      {scenario.appliedPayoutType === "half_base" && (
        <>
          <Rule />
          <div className="bg-gradient-to-r from-amber-50/60 to-white px-5 py-3">
            <p className="text-[13px] font-medium leading-relaxed text-slate-600">
              <span className="font-semibold text-amber-700">{t("note")}: </span>
              {t("halfBaseAppliedNote")}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

const StarRating: React.FC<{
  value: number;
  onChange: (v: number) => void;
  labels: string[];
}> = ({ value, onChange, labels }) => {
  const [hover, setHover] = useState(0);
  const active = hover || value;

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHover(star)}
            onMouseLeave={() => setHover(0)}
            className="p-0.5 transition-transform hover:scale-110 focus:outline-none"
          >
            {active >= star ? (
              <StarSolidIcon className="h-7 w-7 text-amber-400" />
            ) : (
              <StarOutlineIcon className="h-7 w-7 text-stone-300" />
            )}
          </button>
        ))}
      </div>
      <p className="h-4 text-[13px] font-medium text-slate-500">
        {active > 0 ? labels[active] : ""}
      </p>
    </div>
  );
};

const AcceptanceStatus: React.FC<{
  managerAccepted: boolean;
  fighterAccepted: boolean;
  t: any;
}> = ({ managerAccepted, fighterAccepted, t }) => (
  <div className="border-t border-slate-200 pt-3">
    <p className="mb-2 flex items-center gap-1.5 text-[13px] font-medium text-slate-600">
      <CheckCircleIcon className="h-4 w-4 text-sky-500" />
      {managerAccepted && fighterAccepted
        ? t("bothConfirmed")
        : managerAccepted
          ? t("managerConfirmed")
          : t("fighterConfirmed")}
    </p>
    <div className="flex gap-4 pl-5">
      {[
        {
          key: "manager",
          accepted: managerAccepted,
          label: t("manager"),
        },
        {
          key: "fighter",
          accepted: fighterAccepted,
          label: t("fighter"),
        },
      ].map(({ key, accepted, label }) => (
        <span
          key={key}
          className={`flex items-center gap-1.5 text-[13px] font-medium ${
            accepted ? "text-slate-700" : "text-slate-500"
          }`}
        >
          <span
            className={`inline-block h-1.5 w-1.5 rounded-full ${
              accepted ? "bg-sky-500" : "bg-slate-300"
            }`}
          />
          {label}
        </span>
      ))}
    </div>
  </div>
);

const ResultRatingsTab: React.FC<ResultRatingsTabProps> = ({
  deal,
  activeTab,
  currentUserId,
  isManager,
  hasUserRated,
  refreshDeal,
  tr, // 👈 புது parameter destructure பண்ணிட்டோம்
}) => {
  const { t } = useTranslation("viewDeal");
  const navigate = useNavigate();
  const [selectedResult, setSelectedResult] = useState<FightResultType>(null);
  const [isUpdatingResult, setIsUpdatingResult] = useState(false);

  const [isDisputeModalOpen, setIsDisputeModalOpen] = useState(false);
  const [counterResult, setCounterResult] = useState<FightResultType>(null);
  const [counterDescription, setCounterDescription] = useState("");
  const [counterAttachments, setCounterAttachments] = useState<File[]>([]);
  const [isSubmittingCounter, setIsSubmittingCounter] = useState(false);

  const [disputeDropdownOpen, setDisputeDropdownOpen] = useState(false);
  const disputeDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        disputeDropdownRef.current &&
        !disputeDropdownRef.current.contains(e.target as Node)
      ) {
        setDisputeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fightResult = getFightResult(deal);
  const managerAccepted = isManagerAccepted(deal);
  const fighterAccepted = isFighterAccepted(deal);
  const templateLabels = useMemo(
    () => buildTemplateLabels(deal?.templateJson),
    [deal?.templateJson],
  );
  const resultOptions = useMemo(
    () => buildResultOptions(deal, t),
    [deal, t],
  );

  const selectedScenario = resolveScenario(deal, selectedResult ?? fightResult);
  const selectedResultLabel = selectedResult
    ? getResultDisplayLabel(
        selectedResult,
        resolveScenario(deal, selectedResult),
        t,
      )
    : "";
  const counterResultLabel = counterResult
    ? getResultDisplayLabel(
        counterResult,
        resolveScenario(deal, counterResult),
        t,
      )
    : "";

  const isDrawResult =
    (selectedResult ?? fightResult ?? "").toUpperCase() === "DRAW";

  const displayCurrency = deal?.dealPolicySummaryDto?.currency ?? "-";
  const evidenceUploadLabel = VIDEO_UPLOAD_ENABLED
    ? t("attachImagesOrVideos")
    : t("attachImages");
  const evidenceFormatLabel = VIDEO_UPLOAD_ENABLED
    ? t("supportedEvidenceFormats")
    : t("supportedEvidenceFormatsWithoutVideo");

  useEffect(() => {
    setSelectedResult((fightResult as FightResultType) || null);
  }, [fightResult]);

  const submitFightResult = async () => {
    if (!selectedResult) {
      toast.error(t("pleaseSelectResult"));
      return;
    }
    setIsUpdatingResult(true);
    try {
      await updateFightResultOnly(deal.id, selectedResult, false);
      toast.success(t("resultUpdated"));
      await refreshDeal();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t("failedToUpdateResult"));
    } finally {
      setIsUpdatingResult(false);
    }
  };

  const handleConfirmResult = async () => {
    if (!selectedResult) return;
    setIsUpdatingResult(true);
    try {
      await updateFightResultOnly(deal.id, selectedResult, true);
      toast.success(t("resultConfirmed"));
      await refreshDeal();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t("failedToConfirmResult"));
    } finally {
      setIsUpdatingResult(false);
    }
  };

  const handleSubmitDispute = async () => {
    if (!deal || !selectedResult || !counterResult) return;
    setIsSubmittingCounter(true);
    try {
      let evidenceFiles: FileMetadataDto[] = [];
      if (counterAttachments.length > 0) {
        evidenceFiles = await uploadMultipleFile(counterAttachments, "file");
      }
      const payload: DealDisputeRequestDto = {
        dealId: deal.id,
        preferredResult: counterResult,
        description: counterDescription.trim(),
        evidenceFiles,
      };
      await updateFightResultOnly(deal.id, selectedResult, false, payload);
      toast.success(t("disputeSubmitted"));
      setIsDisputeModalOpen(false);
      setCounterResult(null);
      setCounterDescription("");
      setCounterAttachments([]);
      await refreshDeal();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t("failedToSubmitDispute"));
    } finally {
      setIsSubmittingCounter(false);
    }
  };

  const handleCounterAttachmentsChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(event.target.files || []);
    const allowedFiles = VIDEO_UPLOAD_ENABLED
      ? files
      : files.filter((file) => !isVideoFile(file));

    setCounterAttachments(allowedFiles);
    event.target.value = "";
  };

  return (
    <>
      <Modal
        isOpen={isDisputeModalOpen}
        onOpenChange={setIsDisputeModalOpen}
        placement="center"
        backdrop="blur"
        size="md"
        hideCloseButton
      >
        <ModalContent className="mx-4 overflow-hidden rounded-2xl shadow-xl">
          {(onClose) => (
            <>
              <div className="border-b border-gray-100 px-6 pb-4 pt-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-gray-100">
                      <ShieldExclamationIcon className="h-4 w-4 text-gray-500" />
                    </div>
                    <div>
                      <h3 className="text-[14px] font-bold text-gray-900">
                        {t("submitDispute")}
                      </h3>
                      <p className="mt-0.5 text-[11px] text-gray-400">
                        {t("disputeSubtitle")}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              <ModalBody className="space-y-3 bg-white px-6 py-4">
                {/* Fight Result */}
                <div className="space-y-1.5">
                  <label className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                    {t("yourFightResult")}
                  </label>

                  <div ref={disputeDropdownRef} className="relative">
                    <button
                      type="button"
                      onClick={() => setDisputeDropdownOpen((p) => !p)}
                      className="flex w-full items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-[13px] font-medium text-gray-700 transition hover:border-gray-300 hover:bg-white focus:outline-none focus:ring-2 focus:ring-gray-200"
                    >
                      <span
                        className={
                          counterResult
                            ? "font-semibold text-gray-900"
                            : "text-gray-400"
                        }
                      >
                        {counterResult
                          ? counterResultLabel
                          : t("selectYourResult")}
                      </span>
                      <svg
                        className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform duration-150 ${
                          disputeDropdownOpen ? "rotate-180" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {disputeDropdownOpen && (
                      <div className="absolute left-0 top-full z-50 mt-1 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
                        {resultOptions.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => {
                              setCounterResult(option.value);
                              setDisputeDropdownOpen(false);
                            }}
                            className={`flex w-full items-center justify-between px-3.5 py-2.5 text-left text-[13px] transition-colors ${
                              counterResult === option.value
                                ? "bg-gray-100 font-semibold text-gray-900"
                                : "text-gray-600 hover:bg-gray-50"
                            }`}
                          >
                            {option.label}
                            {counterResult === option.value && (
                              <CheckIcon className="h-3.5 w-3.5 text-gray-500" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {counterResult === "DRAW" &&
                    selectedScenario?.appliedPayoutType === "half_base" && (
                      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                        <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                        <p className="text-[12px] font-medium text-amber-700">
                          {t("drawPayoutWarning")}
                        </p>
                      </div>
                    )}
                </div>

                <div className="space-y-1.5">
                  <label className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                    {t("description")}{" "}
                    <span className="normal-case text-gray-400">*</span>
                  </label>
                  <textarea
                    value={counterDescription}
                    onChange={(e) => setCounterDescription(e.target.value)}
                    placeholder={t("explainWhatHappened")}
                    rows={3}
                    className="w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-[13px] font-medium text-gray-800 outline-none placeholder:text-gray-300 transition focus:border-gray-400 focus:bg-white focus:ring-2 focus:ring-gray-100"
                  />
                  <p className="text-right text-[11px] text-gray-300">
                    {t("charsCount", { count: counterDescription.length })}
                  </p>
                </div>

                <div className="space-y-1.5">
                  <label className="block text-[12px] font-semibold uppercase tracking-wide text-gray-500">
                    {t("evidence")}{" "}
                    <span className="normal-case font-normal text-gray-300">
                      ({t("optional")})
                    </span>
                  </label>

                  <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-2.5 transition-all hover:border-gray-400 hover:bg-white">
                    <input
                      type="file"
                      multiple
                      accept={VIDEO_UPLOAD_ENABLED ? "image/*,video/*" : "image/*"}
                      className="hidden"
                      onChange={handleCounterAttachmentsChange}
                    />
                    <PaperClipIcon className="h-4 w-4 shrink-0 text-gray-400" />
                    <div className="min-w-0">
                      <p className="text-[12px] font-semibold text-gray-600">
                        {counterAttachments.length > 0
                          ? t("filesAttached", { count: counterAttachments.length })
                          : evidenceUploadLabel}
                      </p>
                      <p className="text-[11px] text-gray-400">
                        {evidenceFormatLabel}
                      </p>
                    </div>
                  </label>

                  {counterAttachments.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-0.5">
                      {counterAttachments.map((file, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-600"
                        >
                          {file.name.length > 18
                            ? file.name.slice(0, 16) + "…"
                            : file.name}
                          <button
                            type="button"
                            onClick={() =>
                              setCounterAttachments((prev) =>
                                prev.filter((_, idx) => idx !== i),
                              )
                            }
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </ModalBody>

              <ModalFooter className="flex gap-2 border-t border-gray-100 bg-gray-50/50 px-6 py-3">
                <Button
                  variant="flat"
                  size="sm"
                  onPress={onClose}
                  className="h-8 rounded-lg border border-gray-200 bg-white px-4 text-[12px] font-semibold text-gray-600 shadow-none hover:bg-gray-100"
                >
                  {t("cancel")}
                </Button>
                <Button
                  size="sm"
                  isLoading={isSubmittingCounter}
                  isDisabled={!counterResult || !counterDescription.trim()}
                  onPress={handleSubmitDispute}
                  className="h-8 rounded-lg bg-gray-900 px-4 text-[12px] font-semibold text-white hover:bg-gray-700 disabled:opacity-40"
                >
                  {t("submitDispute")}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {activeTab === "result" && (
        <div className="mx-auto max-w-3xl px-1 py-5">
          <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-[0.95fr_1.05fr]">
            {/* LEFT */}
            <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
              {isManager ? (
                <>
                  <div className="space-y-1.5">
                    <Label>{t("selectResult")}</Label>
                    <p className="text-[14px] font-medium text-slate-600">
                      {fightResult
                        ? t("resultAlreadySet")
                        : t("chooseOutcome")}
                    </p>
                    <div className="max-w-sm">
                      <SelectDropdown
                        value={selectedResult}
                        onChange={setSelectedResult}
                        options={resultOptions}
                        disabled={!!fightResult}
                        placeholder={t("selectResult")}
                      />
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      onClick={submitFightResult}
                      isLoading={isUpdatingResult}
                      isDisabled={!!fightResult || !selectedResult}
                      size="sm"
                      className="h-10 min-w-[150px] rounded-xl bg-sky-600 px-4 text-[14px] font-semibold text-white hover:bg-sky-500"
                    >
                      {fightResult
                        ? t("resultSubmitted")
                        : t("submitResult")}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label>{t("currentResult")}</Label>
                    <div className="mt-1.5">
                      {selectedResult ? (
                        <ResultBadge label={selectedResultLabel} />
                      ) : (
                        <span className="text-[15px] font-medium text-slate-500">
                          {t("noResultYet")}
                        </span>
                      )}
                    </div>
                  </div>

                  {selectedResult &&
                    !fighterAccepted &&
                    deal.status === "RESULT_UPDATED" && (
                      <div className="flex gap-2">
                        <Button
                          onClick={handleConfirmResult}
                          isLoading={isUpdatingResult}
                          size="sm"
                          className="h-10 flex-1 rounded-xl border border-emerald-200 bg-emerald-50 px-3 text-[14px] font-semibold text-emerald-800 hover:bg-emerald-100"
                          startContent={<CheckCircleIcon className="h-4 w-4" />}
                        >
                          {t("confirmResult")}
                        </Button>

                        <Button
                          variant="bordered"
                          size="sm"
                          className="h-10 flex-1 rounded-xl border border-rose-200 bg-rose-50 px-3 text-[14px] font-semibold text-rose-800 hover:bg-rose-100"
                          startContent={
                            <ExclamationTriangleIcon className="h-4 w-4 text-rose-500" />
                          }
                          onClick={() => {
                            setCounterResult(null);
                            setCounterDescription("");
                            setCounterAttachments([]);
                            setIsDisputeModalOpen(true);
                          }}
                          isDisabled={isUpdatingResult}
                        >
                          {t("rejectresult")}
                        </Button>
                      </div>
                    )}

                </>
              )}

              {(managerAccepted || fighterAccepted) && (
                <AcceptanceStatus
                  managerAccepted={managerAccepted}
                  fighterAccepted={fighterAccepted}
                  t={t}
                />
              )}
            </div>

            <div>
              {selectedScenario ? (
                <ScenarioCard
                  scenario={selectedScenario}
                  baseAmount={deal?.dealPolicySummaryDto?.baseAmount}
                  currency={displayCurrency}
                  isManager={isManager}
                  isDraw={isDrawResult}
                  t={t}
                  templateLabels={templateLabels}
                  tr={tr}   
                />
              ) : (
                <div className="flex min-h-[160px] items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-gradient-to-br from-sky-50/60 to-indigo-50/40 p-6 text-center">
                  <p className="text-[15px] font-medium leading-relaxed text-slate-500">
                    {t("selectResultToSeeAmount")}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === "ratings" && (
        <div className="mx-auto max-w-4xl px-2 py-5">
          {deal.ratingResponse && deal.ratingResponse.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {deal.ratingResponse.map((r) => {
                const profileId = r.profile?.id;

                return (
                <div
                  key={r.id}
                  className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:shadow-md hover:border-sky-200"
                >
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      onClick={() =>
                        profileId &&
                        navigate(`/profile?profileId=${profileId}`)
                      }
                      disabled={!profileId}
                      className="mt-0.5 shrink-0 disabled:cursor-default"
                    >
                      <Avatar
                        size="sm"
                        src={r.profile?.profileImage}
                        className="shrink-0"
                      />
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <button
                            type="button"
                            onClick={() =>
                              profileId &&
                              navigate(`/profile?profileId=${profileId}`)
                            }
                            disabled={!profileId}
                            className="text-left text-[15px] font-semibold text-slate-900 hover:underline disabled:cursor-default"
                          >
                            {r.profile
                              ? `${r.profile.firstName} ${r.profile.lastName}`
                              : t("anonymous")}
                          </button>
                          <div className="mt-1 flex items-center gap-2">
                            <div className="flex items-center gap-0.5">
                              {[1, 2, 3, 4, 5].map((s) => (
                                <StarSolidIcon
                                  key={s}
                                  className={`h-4 w-4 ${
                                    s <= r.rating
                                      ? "text-amber-400"
                                      : "text-stone-200"
                                  }`}
                                />
                              ))}
                            </div>
                            <span className="text-[13px] font-semibold text-slate-600">
                              {r.rating}/5
                            </span>
                          </div>
                        </div>
                        <span className="text-[12px] font-medium text-slate-500">
                          {new Date(r.createdAt).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                        </span>
                      </div>
                      {r.feedback && (
                        <p className="mt-2 border-l-2 border-sky-200 pl-3 text-[14px] font-medium text-slate-700">
                          {tr(r.feedback)}
                          {/* 👆 OLD-ல இது இப்படி இருந்துச்சு: {r.feedback}
                             NEW-ல tr() போட்டோம் - feedback backend
                             free text ஆனதால tr() apply பண்ணிட்டோம் */}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-sky-50/40 py-14 text-center">
              <StarOutlineIcon className="mx-auto mb-3 h-8 w-8 text-stone-300" />
              <p className="text-[15px] font-medium text-slate-500">
                {t("noRatingsYet")}
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ResultRatingsTab;